import React, { useState, useMemo, useEffect } from 'react';
import { useReactTable, getCoreRowModel, getPaginationRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import PanelExportButton from './PanelExportButton';
import numberFormatter from '../../utils/numberFormatter';

const PanelListPlay = ({ panels, campaignType, campaignName, parameters, header }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [tableData, setTableData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const filterFields = ["panelId", "environment", "address", "city"];

    useEffect(() => {
        const sortedPanels = [...panels].sort((a, b) => (a.panelId > b.panelId ? 1 : -1));
        setTableData(sortedPanels);
        setFilteredData(sortedPanels);
    }, [panels]);

    const columns = useMemo(() => [
        { accessorKey: 'panelId', header: 'Panel Id', cell: info => info.getValue(), enableSorting: true },
        { accessorKey: 'environment', header: 'Environment', cell: info => info.getValue(), enableSorting: true },
        { accessorKey: 'address', header: 'Address', cell: info => info.getValue(), enableSorting: true },
        { accessorKey: 'city', header: 'City', cell: info => info.getValue(), enableSorting: true },
        {
            accessorKey: 'soV',
            header: 'SOV',
            cell: info => numberFormatter.numberWithOneDecAndProcent(info.getValue()),
            enableSorting: true
        }
    ], []);

    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        initialState: {
            pagination: {
                pageSize: filteredData.length > 1000 ? Math.ceil(filteredData.length / 10) : 100
            }
        },
        manualPagination: showAll,
    });

    useEffect(() => {
        const newFilteredData = tableData.filter(row =>
            filterFields.some(field =>
                row[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
        setFilteredData(newFilteredData);
    }, [searchTerm, tableData]);

    return (
        <div className="row playPanelList hidden-print">
            <div className="col-md-12" style={{ padding: "0px" }}>
                <h4>{header}</h4>
            </div>
            <div style={{ marginBottom: '10px' }}>
                <input
                    type="text"
                    placeholder="Filter"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-control"
                />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: '10px' }}>
                <PanelExportButton
                    panels={filteredData}
                    campaignType={campaignType}
                    campaignName={campaignName}
                    allowAddressExport={true}
                    exportPanelSOVValue={parameters.clientSettings.exportPanelSOVValue}
                />
            </div>
            <div className="table-responsive">
                <table className="table">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => {
                                    const sortOrder = header.column.getIsSorted();
                                    return (
                                        <th key={header.id} onClick={header.column.getToggleSortingHandler()} className="sortable-header">
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            <span className="sort-arrow">
                                                {sortOrder === 'asc' ? ' ↑' : sortOrder === 'desc' ? ' ↓' : ' ↑↓'}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {(showAll ? filteredData.map(row => table.getRowModel().rows.find(r => r.original === row)) : table.getRowModel().rows)
                            .filter(row => row)
                            .map(row => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                    ))}
                                </tr>
                            ))}
                    </tbody>

                </table>
            </div>
            <div className="pagination-container">
                {!showAll ? (
                    <>
                        <button className="btn btn-play pagination-button" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                            &lt;
                        </button>
                        <span> Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()} </span>
                        <button className="btn btn-play pagination-button" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                            &gt;
                        </button>
                        <button className="btn btn-play show-all-button trigger-pagination" onClick={() => setShowAll(true)}>
                            Show All
                        </button>
                    </>
                ) : (
                    <button className="btn btn-play pagination-button trigger-pagination" onClick={() => setShowAll(false)}>
                        Back to Paginated View
                    </button>
                )}
            </div>
        </div>
    );
};

export default React.memo(PanelListPlay);